
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';

interface PaymentSummaryProps {
  basePrice: number;
  preferencesCharges?: { [key: string]: number };
  convenienenceFee?: number;
  couponDiscount?: number;
  couponCode?: string;
  showRewards?: boolean;
}

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  basePrice,
  preferencesCharges = {},
  convenienenceFee = 30,
  couponDiscount = 25,
  couponCode = "CHALO25",
  showRewards = true
}) => {
  const preferencesTotal = Object.values(preferencesCharges).reduce((sum, charge) => sum + charge, 0);
  const riderEarnings = basePrice + preferencesTotal;
  const platformCharges = convenienenceFee - couponDiscount;
  const netEarnings = riderEarnings - platformCharges;

  return (
    <Card>
      <CardContent className="p-4">
        <h4 className="font-semibold text-gray-800 mb-4">Payment Summary</h4>
        
        {/* Rider Earnings Section */}
        <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <h5 className="font-medium text-green-800 mb-2 text-sm">Your Earnings</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Base Ride Fee</span>
              <span className="text-green-700 font-medium">₹{basePrice}</span>
            </div>
            
            {/* Preferences Charges */}
            {Object.entries(preferencesCharges).map(([pref, charge]) => (
              <div key={pref} className="flex justify-between">
                <span>{pref} charge</span>
                <span className="text-green-700 font-medium">₹{charge}</span>
              </div>
            ))}
            
            <div className="flex justify-between font-medium pt-2 border-t border-green-300">
              <span>Total Earnings</span>
              <span className="text-green-800">₹{riderEarnings}</span>
            </div>
          </div>
        </div>

        {/* Platform Charges Section */}
        <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
          <h5 className="font-medium text-orange-800 mb-2 text-sm">Platform Charges</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Chalo Service Fee</span>
              <span className="text-orange-700 font-medium">₹{convenienenceFee}</span>
            </div>
            
            {showRewards && (
              <div className="flex justify-between text-green-600">
                <span>Coupon Discount ({couponCode})</span>
                <span>-₹{couponDiscount}</span>
              </div>
            )}
            
            <div className="flex justify-between font-medium pt-2 border-t border-orange-300">
              <span>Platform Cost</span>
              <span className="text-orange-800">₹{platformCharges}</span>
            </div>
          </div>
        </div>

        {/* Net Earnings */}
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex justify-between font-bold text-base">
            <span className="text-blue-800">Your Net Earnings</span>
            <span className="text-blue-800">₹{netEarnings}</span>
          </div>
          {showRewards && (
            <div className="flex justify-between text-blue-600 text-sm mt-1">
              <span>Chalo Miles Earned</span>
              <span>+250 Miles</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
